import { Directive, ElementRef, HostListener, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Directive({
    selector: '[phoneMask]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PhoneMaskDirective),
            multi: true,
        },
    ],
    standalone: false,
})
export class PhoneMaskDirective implements ControlValueAccessor {
    private onChange: (value: string) => void = () => {};
    private onTouched: () => void = () => {};

    public constructor(private el: ElementRef<HTMLInputElement>) {}

    @HostListener('input', ['$event'])
    public onInput(event: InputEvent): void {
        const input = this.el.nativeElement;
        // Сохраняем текущую позицию курсора и количество цифр до курсора
        const cursorPos = input.selectionStart || 0;
        const valueBefore = input.value;
        const digitsBeforeCursor = (
            valueBefore.slice(0, cursorPos).match(/\d/g) || []
        ).length;
        let digits = input.value.replace(/\D/g, '');
        // форматирование ввода
        if (digits.startsWith('8')) {
            digits = '7' + digits.slice(1);
        }

        if (digits.length > 0 && !digits.startsWith('7')) {
            digits = '7' + digits;
        }

        digits = digits.slice(0, 11);

        const formatted = this.format(digits);
        input.value = formatted;
        // Восстанавливаем позицию курсора
        let newCursorPos = 0;
        let digitCount = 0;
        for (let i = 0; i < formatted.length; i++) {
            if (/\d/.test(formatted[i])) {
                digitCount++;
            }
            if (digitCount === digitsBeforeCursor) {
                newCursorPos = i + 4;
                break;
            }
        }
        // Если не нашли позицию, ставим в конец
        if (newCursorPos === 0) {
            newCursorPos = formatted.length;
        }

        setTimeout(() => {
            input.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);

        this.onChange(digits);
    }

    @HostListener('blur')
    public onBlur(): void {
        this.onTouched();
    }

    @HostListener('keydown', ['$event'])
    public onKeyDown(event: KeyboardEvent): void {
        const input = this.el.nativeElement;

        const allowed = [
            'Tab',
            'Escape',
            'Enter',
            'ArrowLeft',
            'ArrowRight',
            'ArrowUp',
            'ArrowDown',
            'Home',
            'End',
        ];

        if (allowed.includes(event.key)) return;

        if (event.ctrlKey || event.metaKey) return;

        // Обработка Backspace
        if (event.key === 'Backspace') {
            // Проверяем, есть ли выделение
            const selectionStart = input.selectionStart || 0;
            const selectionEnd = input.selectionEnd || 0;

            // Если есть выделение, даем стандартному поведению удалить выделенное
            if (selectionStart !== selectionEnd) {
                return;
            }
            const cursorPos = input.selectionStart || 0;
            const value = input.value;

            // Если курсор в начале - ничего не делаем
            if (cursorPos === 0) {
                event.preventDefault();
                return;
            }

            // Проверяем символ перед курсором
            const charBefore = value[cursorPos - 1];

            if (/\d/.test(charBefore)) {
                // Если перед курсором цифра - даем стандартному поведению удалить её
                return;
            } else {
                // Если перед курсором не цифра (скобка, пробел, дефис)
                event.preventDefault();

                // Находим предыдущую цифру
                let prevDigitPos = cursorPos - 1;
                while (prevDigitPos >= 0 && !/\d/.test(value[prevDigitPos])) {
                    prevDigitPos--;
                }

                if (prevDigitPos >= 0 && /\d/.test(value[prevDigitPos])) {
                    // Удаляем цифру, изменяя value напрямую
                    const newValue =
                        value.slice(0, prevDigitPos) +
                        value.slice(prevDigitPos + 1);
                    input.value = newValue;

                    // Обновляем digits в контроле
                    const digits = newValue.replace(/\D/g, '');
                    this.onChange(digits);

                    // Устанавливаем курсор на позицию удаленной цифры
                    setTimeout(() => {
                        input.setSelectionRange(prevDigitPos, prevDigitPos);
                    }, 0);
                } else {
                    // Если цифр нет, просто ставим курсор в начало
                    setTimeout(() => {
                        input.setSelectionRange(0, 0);
                    }, 0);
                }
            }
            return;
        }

        if (!/^\d$/.test(event.key)) {
            event.preventDefault();
        }
    }

    private format(digits: string): string {
        if (digits.length === 0) return '';

        let result = '+7';
        if (digits.length > 1) result += ' (' + digits.slice(1, 4);
        if (digits.length >= 4) result += ')';
        if (digits.length > 4) result += ' ' + digits.slice(4, 7);
        if (digits.length > 7) result += '-' + digits.slice(7, 9);
        if (digits.length > 9) result += '-' + digits.slice(9, 11);

        return result;
    }

    // ControlValueAccessor
    public writeValue(value: string): void {
        if (value) {
            const digits = value.replace(/\D/g, '');
            this.el.nativeElement.value = this.format(digits);
        } else {
            this.el.nativeElement.value = '';
        }
    }

    public registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.el.nativeElement.disabled = isDisabled;
    }
}
